/**
 * Language mapping configuration
 * Maps language codes used in routing to actual folder names in the messages directory
 */

import { DEFAULT_LOCALE } from "@/app/constants"

export interface LanguageMapping {
	[key: string]: {
		folder: string
		name: string
		nativeName: string
		flag: string
	}
}

export const languageMapping: LanguageMapping = {
	en: {
		folder: 'en',
		name: 'English',
		nativeName: 'English',
		flag: 'gb',
	},
	ar: {
		folder: 'ar-SA',
		name: 'Arabic',
		nativeName: 'العربية',
		flag: 'sa',
	},
	de: {
		folder: 'de-DE',
		name: 'German',
		nativeName: 'Deutsch',
		flag: 'de',
	},
	'es-LA': {
		folder: 'es-419',
		name: 'Spanish (Latin America)',
		nativeName: 'Español (Latinoamérica)',
		flag: 'es',
	},
	es: {
		folder: 'es-ES',
		name: 'Spanish',
		nativeName: 'Español',
		flag: 'es',
	},
	fr: {
		folder: 'fr-FR',
		name: 'French',
		nativeName: 'Français',
		flag: 'fr',
	},
	he: {
		folder: 'he-IL',
		name: 'Hebrew',
		nativeName: 'עברית',
		flag: 'il',
	},
	it: {
		folder: 'it-IT',
		name: 'Italian',
		nativeName: 'Italiano',
		flag: 'it',
	},
	ja: {
		folder: 'ja-JP',
		name: 'Japanese',
		nativeName: '日本語',
		flag: 'jp',
	},
	ko: {
		folder: 'ko-KR',
		name: 'Korean',
		nativeName: '한국어',
		flag: 'kr',
	},
	nl: {
		folder: 'nl-NL',
		name: 'Dutch',
		nativeName: 'Nederlands',
		flag: 'nl',
	},
	no: {
		folder: 'no-NO',
		name: 'Norwegian',
		nativeName: 'Norsk',
		flag: 'no',
	},
	pl: {
		folder: 'pl-PL',
		name: 'Polish',
		nativeName: 'Polski',
		flag: 'pl',
	},
	'pt-BR': {
		folder: 'pt-BR',
		name: 'Portuguese (Brazil)',
		nativeName: 'Português (Brasil)',
		flag: 'br',
	},
	pt: {
		folder: 'pt-PT',
		name: 'Portuguese',
		nativeName: 'Português',
		flag: 'pt',
	},
	ro: {
		folder: 'ro-RO',
		name: 'Romanian',
		nativeName: 'Română',
		flag: 'ro',
	},
	ru: {
		folder: 'ru-RU',
		name: 'Russian',
		nativeName: 'Русский',
		flag: 'ru',
	},
	sv: {
		folder: 'sv-SE',
		name: 'Swedish',
		nativeName: 'Svenska',
		flag: 'se',
	},
	tr: {
		folder: 'tr-TR',
		name: 'Turkish',
		nativeName: 'Türkçe',
		flag: 'tr',
	},
	vi: {
		folder: 'vi-VN',
		name: 'Vietnamese',
		nativeName: 'Tiếng Việt',
		flag: 'vn',
	},
	zh: {
		folder: 'zh-TW',
		name: 'Chinese (Traditional)',
		nativeName: '繁體中文',
		flag: 'tw',
	},
	// Legacy aliases for backward compatibility
	cz: {
		folder: 'cs-CZ',
		name: 'Czech',
		nativeName: 'Čeština',
		flag: 'cz',
	},
	gr: {
		folder: 'el-GR',
		name: 'Greek',
		nativeName: 'Ελληνικά',
		flag: 'gr',
	},
	th: {
		folder: 'th-TH',
		name: 'Thai',
		nativeName: 'ไทย',
		flag: 'th',
	},
	hu: {
		folder: 'hu-HU',
		name: 'Hungarian',
		nativeName: 'Magyar',
		flag: 'hu',
	},
	fi: {
		folder: 'fi-FI',
		name: 'Finnish',
		nativeName: 'Suomi',
		flag: 'fi',
	},
}

export const countryLanguageMap: any = {
	ar: 'SA',
	he: 'IL',
	zh: 'HK',
	'pt_br': 'BR',
	'zh_tw': 'TW',
	vi: 'VN',
	'es_la': 'ES',
	ja: 'JP',
	ko: 'KR',
	sv: 'SE',
}

export const getLanguageCode = (lng: string): string => {
	const fallback = DEFAULT_LOCALE.split('_')[0];
	if (!lng) {
		return fallback;
	}
	const resolved = resolveLanguageCode(lng);
	return (resolved.split('-')[0] || fallback);
}

export const getUrlLanguageCode = (code: Extract<keyof LanguageMapping, string>): string => {
	return code.replace(/-/g, '_').toLocaleLowerCase();
}

/**
 * Normalize an incoming language code to the canonical mapping key.
 * - Replaces underscores with dashes
 * - Lowercases the language part, uppercases alpha region (keeps numeric regions)
 * - Maps es-la to es-419 (Latin America Spanish)
 * - Falls back to base language if region-specific key not present
 */
export const resolveLanguageCode = (languageCode?: string): string => {
	if (!languageCode) return DEFAULT_LOCALE;

	const cleaned = languageCode.replace(/_/g, '-')
	const parts = cleaned.split('-')

	let canonical = parts[0]?.toLowerCase() || ''
	if (parts[1]) {
		const region = parts[1]
		const upperRegion = /^[0-9]+$/.test(region) ? region : region.toUpperCase()
		canonical = `${canonical}-${upperRegion}`
	}

	// Check if exact match exists
	if (languageMapping[canonical]) return canonical

	// Fall back to base language
	const base = canonical.split('-')[0]
	if (languageMapping[base]) return base

	return canonical
}

/**
 * Get folder name for a language code
 */
export const getLanguageFolder = (languageCode: string): string => {
	const resolved = resolveLanguageCode(languageCode)
	return languageMapping[resolved]?.folder || resolved
}

/**
 * Get all supported language codes
 */
export const getSupportedLanguages = (): string[] => {
	const keys = Object.keys(languageMapping)
	// Add underscore, lowercase aliases for regioned codes (e.g., fr-CA -> fr_ca)
	const underscoreAliases = keys
		.filter((k) => k.includes('-'))
		.map((k) => k.replace(/-/g, '_').toLowerCase())
	return Array.from(new Set([...keys, ...underscoreAliases]))
}

/**
 * Get language metadata by code
 */
export const getLanguageMetadata = (
	languageCode: string,
): LanguageMapping[string] | undefined => {
	const resolved = resolveLanguageCode(languageCode)
	return languageMapping[resolved]
}

/**
 * Get all languages as an array with their codes
 */
export const getAllLanguages = (): Array<{
	code: string
	folder: string
	name: string
	nativeName: string
	flag: string
}> => {
	return Object.entries(languageMapping).map(([code, data]) => ({
		code,
		...data,
	}))
}
